import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, CreditCard, Wallet, TrendingUp, History, CheckCircle2, Zap, Trash2, Sparkles, Landmark } from "lucide-react";
import { toast } from "sonner";
import { fmt as fmtMoney } from "@/lib/currency";
import BankTransferModal from "./BankTransferModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtTime(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-GB", { hour12: false, day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
}

export default function CreditsPanel() {
    const [account, setAccount] = useState(null);
    const [txns, setTxns] = useState([]);
    const [packs, setPacks] = useState([]);
    const [busy, setBusy] = useState(null);
    const [autoRule, setAutoRule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tabbyStatus, setTabbyStatus] = useState({ configured: false });
    const [cbqStatus, setCbqStatus] = useState({ configured: false });
    const [bankPack, setBankPack] = useState(null); // currently-open bank-transfer pack

    const load = useCallback(async () => {
        try {
            const [a, t, p, r, tb, cb] = await Promise.all([
                axios.get(`${API}/credits/account`),
                axios.get(`${API}/credits/transactions?limit=50`),
                axios.get(`${API}/credits/packages`),
                axios.get(`${API}/credits/autotopup`).catch(() => ({ data: null })),
                axios.get(`${API}/payments/tabby/status`).catch(() => ({ data: { configured: false } })),
                axios.get(`${API}/payments/cbq/status`).catch(() => ({ data: { configured: false } })),
            ]);
            setAccount(a.data);
            setTxns(t.data || []);
            setPacks(p.data.packages || []);
            setAutoRule(r.data);
            setTabbyStatus(tb.data);
            setCbqStatus(cb.data);
        } catch (e) {
            toast.error("Could not load credits");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Resume after Stripe redirect ?credits_session=...
    useEffect(() => {
        const url = new URL(window.location.href);
        const sess = url.searchParams.get("credits_session");
        if (!sess) return;
        url.searchParams.delete("credits_session");
        window.history.replaceState({}, "", url.toString());
        (async () => {
            try {
                const { data } = await axios.get(`${API}/credits/checkout/status/${sess}`);
                if (data.status === "paid") {
                    toast.success("Top-up confirmed — balance updated", { duration: 6000 });
                    await load();
                } else {
                    toast.info(`Payment ${data.status}`);
                }
            } catch (e) {
                toast.error("Could not verify payment");
            }
        })();
    }, [load]);

    // Handle ?autotopup_setup=... redirect from Stripe setup-mode Checkout
    useEffect(() => {
        const url = new URL(window.location.href);
        const sess = url.searchParams.get("autotopup_setup");
        if (!sess) return;
        url.searchParams.delete("autotopup_setup");
        window.history.replaceState({}, "", url.toString());
        if (sess === "cancelled") { toast.info("Card setup cancelled"); return; }
        (async () => {
            try {
                const { data } = await axios.get(`${API}/credits/autotopup/setup/status/${sess}`);
                if (data.status === "succeeded") {
                    toast.success(`Card saved · ${(data.card_brand || "").toUpperCase()} •••• ${data.card_last4 || ""}`);
                    await load();
                } else {
                    toast.info(`Card setup ${data.status}`);
                }
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not verify card setup");
            }
        })();
    }, [load]);

    const buy = async (packId, method) => {
        setBusy(`${packId}-${method}`);
        try {
            const { data } = await axios.post(`${API}/credits/checkout`, { package_id: packId, origin_url: window.location.origin, method });
            if (method === "stripe") {
                window.location.href = data.url;
            } else {
                toast.success(`Bank transfer reference: ${data.reference}`, { duration: 12000 });
                toast.info(data.instructions, { duration: 16000 });
                await load();
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Checkout failed");
            setBusy(null);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>;

    return (
        <div className="space-y-8" data-testid="credits-panel">
            {bankPack && (
                <BankTransferModal
                    amountQar={bankPack.price}
                    packId={bankPack.id}
                    onClose={() => setBankPack(null)}
                    onConfirmed={() => { load(); }}
                />
            )}
            {/* Balance hero */}
            <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#0B1120] to-[#1e3a5f] text-white p-7 relative overflow-hidden">
                <div className="absolute -top-20 -right-12 w-72 h-72 rounded-full bg-cyan-500/20 blur-3xl" />
                <div className="relative">
                    <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-300/80">· Credit balance</div>
                    <div className="mt-2 font-display text-5xl tracking-tight">{fmtMoney(account?.balance_qar || 0, "QAR")}</div>
                    <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-[12.5px] text-white/70">
                        <div><Wallet size={12} className="inline mr-1.5" /> Lifetime topped up · <span className="text-white font-medium">{fmtMoney(account?.lifetime_topup_qar || 0, "QAR")}</span></div>
                        <div><TrendingUp size={12} className="inline mr-1.5" /> Lifetime spent · <span className="text-white font-medium">{fmtMoney(account?.lifetime_spent_qar || 0, "QAR")}</span></div>
                    </div>
                </div>
            </div>

            {/* Packages */}
            <div>
                <div className="flex items-baseline justify-between mb-3">
                    <h3 className="font-display text-xl text-slate-900 tracking-tight">Buy credits</h3>
                    <div className="text-[12px] text-slate-500">Bank transfer (preferred) · CBQ Card · Visa/MC · Tabby BNPL</div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {packs.map((p) => (
                        <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col" data-testid={`credit-pack-${p.id}`}>
                            <div className="flex items-baseline justify-between">
                                <div className="font-mono-tr text-[10px] tracking-[0.22em] uppercase text-slate-500">{p.label}</div>
                                <div className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10.5px] font-semibold">SAVE {p.discount_pct}%</div>
                            </div>
                            <div className="font-display text-3xl tracking-tight text-slate-900 mt-1.5">{fmtMoney(p.face_value, "QAR")}</div>
                            <div className="text-[12.5px] text-slate-500 mt-0.5">credits</div>
                            <div className="text-[13.5px] text-slate-700 mt-3">
                                Pay <span className="font-display text-xl text-cyan-700 font-semibold">{fmtMoney(p.price, "QAR")}</span>
                            </div>
                            <div className="text-[11px] text-slate-400 mt-0.5">Bonus QAR {(p.face_value - p.price).toLocaleString()}</div>
                            <div className="mt-auto pt-4 space-y-2">
                                <button onClick={() => setBankPack(p)} disabled={!!busy} data-testid={`pay-bank-${p.id}`}
                                    className="w-full py-2.5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 text-[13px] font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-50 relative">
                                    <Landmark size={14} /> Bank transfer
                                    <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 rounded-full bg-emerald-900 text-emerald-50 text-[9px] font-bold tracking-wide uppercase shadow">Preferred</span>
                                </button>
                                {cbqStatus.configured ? (
                                    <button disabled data-testid={`pay-cbq-${p.id}`}
                                        className="w-full py-2.5 rounded-xl bg-[#0B1120] text-white hover:bg-slate-800 text-[13px] font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                                        <CreditCard size={14} /> CBQ Card
                                    </button>
                                ) : (
                                    <DisabledPayBtn icon={CreditCard} label="CBQ Card · coming soon" testid={`pay-cbq-${p.id}`} />
                                )}
                                <button onClick={() => buy(p.id, "stripe")} disabled={!!busy} data-testid={`pay-stripe-${p.id}`}
                                    className="w-full py-2.5 rounded-xl bg-white border border-slate-300 text-slate-800 hover:bg-slate-50 text-[13px] font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                                    {busy === `${p.id}-stripe` ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />} Visa / Mastercard
                                </button>
                                {tabbyStatus.configured ? (
                                    <button disabled data-testid={`pay-tabby-${p.id}`}
                                        className="w-full py-2.5 rounded-xl bg-[#3FE9C3] text-slate-900 hover:bg-[#34d4af] text-[13px] font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                                        <Sparkles size={14} /> Pay with Tabby
                                    </button>
                                ) : (
                                    <DisabledPayBtn icon={Sparkles} label="Tabby BNPL · coming soon" testid={`pay-tabby-${p.id}`} />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Auto top-up rule */}
            <AutoTopupCard rule={autoRule} packs={packs} onChanged={load} />

            {/* Transactions */}
            <div>
                <h3 className="font-display text-xl text-slate-900 tracking-tight mb-3"><History size={16} className="inline mr-2 text-slate-400" />Activity</h3>
                {txns.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-slate-500 text-[13.5px]">No transactions yet.</div>
                ) : (
                    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                        <table className="w-full text-[13.5px]">
                            <thead className="bg-slate-50 text-[10.5px] font-mono-tr tracking-[0.18em] uppercase text-slate-500">
                                <tr><th className="px-4 py-3 text-left">When</th><th className="px-4 py-3 text-left">Type</th><th className="px-4 py-3 text-left">Reason</th><th className="px-4 py-3 text-right">Δ Credits</th><th className="px-4 py-3 text-right">Balance after</th></tr>
                            </thead>
                            <tbody>
                                {txns.map((t) => (
                                    <tr key={t.id} className="border-t border-slate-100" data-testid={`txn-${t.id}`}>
                                        <td className="px-4 py-3 text-slate-600 font-mono-tr text-[12px]">{fmtTime(t.at)}</td>
                                        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${t.type === "debit" ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-700"}`}>{t.type}</span></td>
                                        <td className="px-4 py-3 text-slate-800">{t.reason}{t.reference ? <span className="text-slate-400 ml-2 font-mono-tr text-[11px]">{t.reference}</span> : null}</td>
                                        <td className={`px-4 py-3 text-right font-medium ${t.delta < 0 ? "text-rose-700" : "text-emerald-700"}`}>{t.delta > 0 ? "+" : ""}{fmtMoney(Math.abs(t.delta), "QAR")}{t.delta < 0 ? "" : ""}</td>
                                        <td className="px-4 py-3 text-right font-medium text-slate-900">{fmtMoney(t.balance_after, "QAR")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}


function DisabledPayBtn({ icon: Icon, label, testid }) {
    return (
        <button disabled data-testid={testid} aria-disabled="true"
            className="w-full py-2.5 rounded-xl bg-slate-100 border border-slate-200 text-slate-500 text-[13px] font-medium inline-flex items-center justify-center gap-1.5 cursor-not-allowed">
            <Icon size={14} /> {label}
        </button>
    );
}


function AutoTopupCard({ rule, packs, onChanged }) {
    const [enabled, setEnabled] = useState(false);
    const [threshold, setThreshold] = useState("5000");
    const [packageId, setPackageId] = useState("pack_20k");
    const [savingCard, setSavingCard] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!rule) return;
        setEnabled(rule.enabled);
        if (rule.threshold_qar) setThreshold(String(rule.threshold_qar));
        if (rule.package_id) setPackageId(rule.package_id);
    }, [rule]);

    if (!rule) return null;
    const hasCard = rule.has_payment_method;

    const addCard = async () => {
        setSavingCard(true);
        try {
            const { data } = await axios.post(`${API}/credits/autotopup/setup`);
            window.location.href = data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start card setup");
            setSavingCard(false);
        }
    };

    const removeCard = async () => {
        if (!window.confirm("Remove saved card and disable auto top-up?")) return;
        try {
            await axios.delete(`${API}/credits/autotopup/card`);
            toast.success("Card removed");
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not remove card");
        }
    };

    const saveRule = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/credits/autotopup`, {
                enabled,
                threshold_qar: Number(threshold) || 0,
                package_id: packageId,
            });
            toast.success(enabled ? "Auto top-up enabled" : "Rule saved");
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    return (
        <div data-testid="autotopup-card" className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-cyan-50 text-cyan-700 grid place-items-center"><Zap size={18} /></div>
                <div className="flex-1 min-w-0">
                    <div className="font-display text-xl tracking-tight text-slate-900">Auto top-up</div>
                    <div className="text-[13px] text-slate-500 mt-0.5">Never get blocked mid-case — we'll automatically refill your credits when the balance drops below your threshold.</div>
                </div>
                {hasCard && (
                    <div className="text-right">
                        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500">Saved card</div>
                        <div className="text-[13.5px] text-slate-900 font-medium mt-0.5">{(rule.card_brand || "card").toUpperCase()} •••• {rule.card_last4 || "••••"}</div>
                        <button onClick={removeCard} className="text-[11.5px] text-rose-600 hover:text-rose-700 mt-1 inline-flex items-center gap-1" data-testid="autotopup-remove-card"><Trash2 size={11} /> Remove</button>
                    </div>
                )}
            </div>

            {!hasCard ? (
                <div className="mt-5">
                    <button onClick={addCard} disabled={savingCard} data-testid="autotopup-add-card"
                        className="px-4 py-2.5 rounded-xl bg-[#0B1120] text-white text-[13.5px] font-medium hover:bg-slate-800 inline-flex items-center gap-2 disabled:opacity-50">
                        {savingCard ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />} Add a card to enable
                    </button>
                    <div className="text-[11.5px] text-slate-500 mt-2.5">We use Stripe to securely save your card. Toothrocket never stores your card details — only the saved-card reference and brand/last4.</div>
                </div>
            ) : (
                <div className="mt-5 grid gap-4 sm:grid-cols-[120px_1fr_1fr_auto] sm:items-end">
                    <label className="flex items-center gap-2.5 cursor-pointer pt-6 sm:pt-0" data-testid="autotopup-enabled">
                        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-4 h-4 accent-cyan-600" />
                        <span className="text-[13.5px] font-medium text-slate-800">Enabled</span>
                    </label>
                    <div>
                        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-1.5">When balance falls below</div>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono-tr text-[11.5px] text-slate-400">QAR</span>
                            <input type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} className="pl-12 pr-3 py-2.5 rounded-lg border border-slate-200 text-[14px] w-full" data-testid="autotopup-threshold" />
                        </div>
                    </div>
                    <div>
                        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-1.5">Buy this pack</div>
                        <select value={packageId} onChange={(e) => setPackageId(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-[14px]" data-testid="autotopup-package">
                            {packs.map((p) => (
                                <option key={p.id} value={p.id}>{p.label} · QAR {p.face_value.toLocaleString()} (pay {p.price.toLocaleString()})</option>
                            ))}
                        </select>
                    </div>
                    <button onClick={saveRule} disabled={saving} data-testid="autotopup-save"
                        className="px-4 py-2.5 rounded-xl bg-cyan-600 text-white text-[13.5px] font-medium hover:bg-cyan-700 inline-flex items-center gap-2 disabled:opacity-50 whitespace-nowrap">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Save rule
                    </button>
                </div>
            )}

            {rule?.enabled && hasCard && (
                <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-100 px-3.5 py-2.5 text-[12.5px] text-emerald-800 flex items-start gap-2">
                    <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
                    <span>Active · We'll charge <b>QAR {(packs.find((p) => p.id === rule.package_id)?.price || 0).toLocaleString()}</b> to your saved card whenever the balance falls below <b>QAR {Number(rule.threshold_qar).toLocaleString()}</b>, adding <b>QAR {(packs.find((p) => p.id === rule.package_id)?.face_value || 0).toLocaleString()}</b> in credits.</span>
                </div>
            )}
        </div>
    );
}
